import { z } from 'zod';

/**
 * F36 — minimal tournament-interest record (F41 stub, S6). At MVP the only
 * status is 'interested'; the surface is a placeholder that persists the
 * interest so F41 (Tournament & Event Listings) can light it up later with the
 * real registration + ticketing flow. Do NOT build the full flow here.
 */

export const TOURNAMENT_REGISTRATION_STATUS_VALUES = ['interested'] as const;
export type TournamentRegistrationStatus = (typeof TOURNAMENT_REGISTRATION_STATUS_VALUES)[number];

export const TOURNAMENT_REF_MAX = 200;
export const TOURNAMENT_NOTE_MAX = 500;

export type TournamentRegistration = {
  readonly id: string;
  readonly teamId: string;
  readonly b2bAccountId: string;
  readonly registeredByUserId: string;
  readonly tournamentRef: string;
  readonly note: string | null;
  readonly status: TournamentRegistrationStatus;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * POST /me/club/teams/:teamId/tournament-interest
 *
 * F41 (#9b) deep-link upgrade: the legacy `tournamentRef` (free-text) path is
 * preserved verbatim for old clients (a `tournament_id IS NULL` 'interested'
 * stub). When BOTH `tournamentId` + `divisionId` are supplied, the route
 * upgrades to a real F41 registration against a live (`published` /
 * `registration_open`) tournament via `TournamentRegistrationService`. The two
 * are mutually optional; the route enforces "both-or-neither".
 */
export const RegisterTournamentInterestInputSchema = z
  .object({
    // Legacy F36 stub fields (kept for back-compat). `tournamentRef` is required
    // only on the legacy path; it's optional here so the F41 deep-link path can
    // omit it (the route fills it from the resolved tournament title).
    tournamentRef: z.string().min(1).max(TOURNAMENT_REF_MAX).optional(),
    note: z.string().max(TOURNAMENT_NOTE_MAX).nullable().optional(),
    // F41 deep-link path — when both present, register against the real tournament.
    tournamentId: z.string().uuid().optional(),
    divisionId: z.string().uuid().optional(),
    /** Acknowledge a skill-range mismatch (F41 soft enforcement). */
    outOfRangeAck: z.boolean().optional(),
  })
  .strict()
  .refine((v) => (v.tournamentId === undefined) === (v.divisionId === undefined), {
    message: 'tournamentId and divisionId must be supplied together',
    path: ['divisionId'],
  })
  .refine((v) => v.tournamentRef !== undefined || v.tournamentId !== undefined, {
    message: 'either tournamentRef (legacy) or tournamentId + divisionId (live) is required',
    path: ['tournamentRef'],
  });
export type RegisterTournamentInterestInput = z.infer<typeof RegisterTournamentInterestInputSchema>;
